import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { buildColorMap } from './colors';
import excludedDaysPlugin from '../../../../../components/analytics/excludedDaysPlugin';
import { EXCLUDED_HATCH_CLASS } from '../../../../../utils/excludedDays';
import { getResourceTimezoneLabel } from '../../../../../utils/datetime';
import { getChartTooltipStyle } from '../../../../../utils/chartDefaults';

const TZ_LABEL = getResourceTimezoneLabel();

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

// Fixed teal for the Predico ensemble line, matching the matrix ENS square.
const ENSEMBLE_COLOR = '#05788a'; // brand-600
const ENSEMBLE_ID = 'ensemble';
// Gold for the oracle envelope (the daily best-forecaster error).
const ORACLE_COLOR = '#d4a017';
// Per-tick readout cap: best N rows listed in the tooltip (plus ensemble
// and oracle); the footer reports how many were cut. Matches
// ErrorByLeadtimeChart so the two tooltips read as one system.
const TOOLTIP_MAX_ROWS = 12;

const isDark = () =>
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark');

// #rrggbb -> rgba(r,g,b,a). The palette and ensemble colour are plain hex, and
// the chart needs per-line alpha for the dim/spotlight states.
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Darker shade of a hex colour (mix toward black), used to set the penalty
// segment/marker apart from the forecaster's own line while keeping its hue.
function darkenRgba(hex, alpha, factor = 0.62) {
  const h = hex.replace('#', '');
  const r = Math.round(parseInt(h.slice(0, 2), 16) * factor);
  const g = Math.round(parseInt(h.slice(2, 4), 16) * factor);
  const b = Math.round(parseInt(h.slice(4, 6), 16) * factor);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Identity shown in the tooltip: admins (real name in payload, not blurred) see
// the name; everyone else the username pseudonym.
function identityOf(row, blurSensitive) {
  if (row.is_ensemble) return 'Predico ensemble';
  const name = (!blurSensitive && row.name) ? row.name : (row.username || row.user_id);
  return row.is_fixed_payment ? `${name} (contracted)` : name;
}

/**
 * Forecasting error over time for the top-N forecasters (plus the Predico
 * ensemble for owners/admins), drawn from the same leaderboard-series payload
 * as the matrix above. Each line is one forecaster's mean error per time
 * bucket; lower is better. The set is every forecaster who held a top-N rank in
 * at least one bucket, so it matches the leaderboard matrix exactly.
 *
 * Selecting a forecaster (the shared trace) dims every other line and keeps
 * that one plus the ensemble in focus, mirroring the matrix spotlight.
 */
export default function ErrorsOverTimeChart({
  data,
  traceUserId = null,
  onTrace = null,
  topN = 10,
  metricLabel = 'RMSE',
  blurSensitive = false,
  headerRight = null,
  showOracle = false,
  colorMap: colorMapProp = null,
  hiddenUserIds = [],
  onHideUser = null,
  onRestoreHidden = null,
}) {
  const dark = isDark();
  const buckets = useMemo(() => data?.buckets || [], [data?.buckets]);
  const penalties = useMemo(() => data?.penalties || [], [data?.penalties]);
  const builtColorMap = useMemo(() => buildColorMap(buckets), [buckets]);
  const colorMap = colorMapProp || builtColorMap;
  const traceActive = Boolean(traceUserId);
  const hiddenIds = useMemo(
    () => new Set(hiddenUserIds.map((id) => String(id))),
    [hiddenUserIds],
  );

  // Excluded (outage) days stay on the axis, empty and hatched.
  const excludedIdx = useMemo(
    () => buckets.flatMap((b, i) => (b.excluded ? [i] : [])),
    [buckets],
  );

  const { chartData, hasData, hasPenalties } = useMemo(() => {
    const labels = buckets.map((b) => b.label);
    // spanGaps joins points across empty days; a segment that would cross an
    // excluded day is hidden instead, so no line runs through it.
    const crossesExcluded = (ctx) => excludedIdx.some(
      (i) => i > ctx.p0DataIndex && i < ctx.p1DataIndex);

    // Penalty charges (peer Q75) aligned to the bucket axis, grouped per
    // forecaster. A penalty day is excluded from the ranked line, so these
    // ride as a separate marker overlay on each forecaster's trajectory.
    const keyIndex = new Map(buckets.map((b, i) => [b.key, i]));
    const penaltyByUser = new Map();
    penalties.forEach((p) => {
      const i = keyIndex.get(p.bucket);
      if (i == null) return;
      let arr = penaltyByUser.get(p.user_id);
      if (!arr) {
        arr = new Array(buckets.length).fill(null);
        penaltyByUser.set(p.user_id, arr);
      }
      arr[i] = Number(p.value);
    });

    // Per-forecaster trajectory (value aligned to bucket index), plus the
    // err/day accumulators used to order the lines by period mean error.
    const byUser = new Map();
    let ensemble = null;

    buckets.forEach((b, i) => {
      (b.rows || []).forEach((row) => {
        if (row.is_ensemble) {
          if (!ensemble) {
            ensemble = { row, points: new Array(buckets.length).fill(null) };
          }
          ensemble.points[i] = Number(row.avg_score);
          return;
        }
        const uid = row.user_id;
        let s = byUser.get(uid);
        if (!s) {
          s = {
            row,
            points: new Array(buckets.length).fill(null),
            errSum: 0,
            daySum: 0,
            topRank: Infinity,
          };
          byUser.set(uid, s);
        }
        s.points[i] = Number(row.avg_score);
        // Best (lowest) rank this forecaster reached in any bucket — drives the
        // top-N membership so the line set matches the matrix above, which
        // shows whoever held a top-N rank slot on any given day.
        if (row.rank != null && row.rank < s.topRank) s.topRank = row.rank;
        // Weight by scored days so the ordering matches the engine's period
        // mean (mirrors the backend summary's err_sum / day_sum).
        const days = Number(row.n_days) || 1;
        s.errSum += Number(row.avg_score) * days;
        s.daySum += days;
        s.row = row; // keep latest identity (name/email may appear later)
      });
    });

    // Match the matrix: every forecaster who held a top-N rank in at least one
    // bucket, ordered by period mean error so colours/draw order stay stable.
    const ranked = Array.from(byUser.values())
      .filter((s) => s.topRank <= topN)
      .filter((s) => !hiddenIds.has(String(s.row.user_id)))
      .sort((a, b) => (a.errSum / a.daySum) - (b.errSum / b.daySum));

    // The traced forecaster is always drawn even if they never cracked the
    // top-N, so the spotlight never blanks out the chart.
    if (traceActive
        && !hiddenIds.has(String(traceUserId))
        && !ranked.some((s) => s.row.user_id === traceUserId)) {
      const extra = byUser.get(traceUserId);
      if (extra) ranked.push(extra);
    }

    let anyPenalty = false;
    const datasets = [];
    ranked.forEach((s) => {
      const uid = s.row.user_id;
      const base = colorMap[uid] || '#94a3b8';
      const lit = !traceActive || uid === traceUserId;
      const identity = identityOf(s.row, blurSensitive);
      const lineAlpha = lit ? 1 : 0.28;

      // Effective trajectory: the forecaster's real error, with penalty days
      // (excluded from the ranked rows) filled by their peer-Q75 charge so the
      // line runs through them. Penalty points are marked so the segments
      // touching them render dashed + darker, set apart from the solid line.
      const pen = penaltyByUser.get(uid);
      const points = s.points.slice();
      const penaltyIdx = [];
      if (pen) {
        pen.forEach((v, i) => {
          if (v == null) return;
          if (points[i] == null) points[i] = v; // fill the penalty-day gap
          penaltyIdx.push(i);
        });
      }
      const isPen = new Set(penaltyIdx);
      if (penaltyIdx.length) anyPenalty = true;

      const darkPoint = lit ? darkenRgba(base, 1) : darkenRgba(base, 0.5);
      datasets.push({
        label: identity,
        data: points,
        borderColor: hexToRgba(base, lineAlpha),
        backgroundColor: hexToRgba(base, lineAlpha),
        borderWidth: uid === traceUserId ? 2.5 : (lit ? 1.75 : 1),
        pointRadius: points.map((_, i) => {
          if (isPen.has(i)) return lit ? 5 : 3;
          return lit ? 2 : 0;
        }),
        pointStyle: points.map((_, i) => (isPen.has(i) ? 'crossRot' : 'circle')),
        pointHoverRadius: 5,
        // A generous invisible target makes thin lines easier to select while
        // preserving their visual weight.
        pointHitRadius: 10,
        // Penalty markers carry the darker shade; normal points the base hue.
        pointBorderColor: points.map((_, i) => (
          isPen.has(i) ? darkPoint : hexToRgba(base, lineAlpha))),
        pointBackgroundColor: points.map((_, i) => (
          isPen.has(i) ? darkPoint : hexToRgba(base, lineAlpha))),
        pointBorderWidth: points.map((_, i) => (isPen.has(i) ? 2 : 1)),
        // Dash + darken any segment that touches a penalty day.
        segment: {
          borderDash: (ctx) => (
            isPen.has(ctx.p0DataIndex) || isPen.has(ctx.p1DataIndex)
              ? [6, 4] : undefined),
          borderColor: (ctx) => {
            if (crossesExcluded(ctx)) return 'transparent';
            return isPen.has(ctx.p0DataIndex) || isPen.has(ctx.p1DataIndex)
              ? darkPoint : undefined;
          },
        },
        spanGaps: true,
        tension: 0.25,
        // Spotlight + ensemble draw on top of the dimmed field.
        order: lit ? 1 : 3,
        meta: { userId: uid, isEnsemble: false, penaltyIdx },
      });
    });

    if (ensemble) {
      datasets.push({
        label: 'Predico ensemble',
        data: ensemble.points,
        borderColor: ENSEMBLE_COLOR,
        backgroundColor: ENSEMBLE_COLOR,
        borderWidth: 2.5,
        borderDash: [6, 4],
        pointRadius: 2,
        pointHoverRadius: 4,
        segment: {
          borderColor: (ctx) => (crossesExcluded(ctx) ? 'transparent' : undefined),
        },
        spanGaps: true,
        tension: 0.25,
        // The ensemble is never dimmed by a forecaster trace.
        order: 0,
        meta: { userId: ENSEMBLE_ID, isEnsemble: true },
      });
    }

    // Oracle envelope: each bucket's lowest forecaster error (the day's winner).
    // The achievable floor if you'd always backed that day's best forecaster.
    if (showOracle) {
      const oraclePoints = buckets.map((b) => {
        let min = null;
        (b.rows || []).forEach((row) => {
          if (row.is_ensemble || row.is_fixed_payment) return;
          const v = Number(row.avg_score);
          if (Number.isNaN(v)) return;
          if (min === null || v < min) min = v;
        });
        return min;
      });
      datasets.push({
        label: 'Oracle (daily best)',
        data: oraclePoints,
        borderColor: ORACLE_COLOR,
        backgroundColor: ORACLE_COLOR,
        borderWidth: 2,
        borderDash: [2, 3],
        pointRadius: 0,
        pointHoverRadius: 4,
        segment: {
          borderColor: (ctx) => (crossesExcluded(ctx) ? 'transparent' : undefined),
        },
        spanGaps: true,
        tension: 0.25,
        // The oracle is a benchmark, never dimmed and never traceable.
        order: 0,
        meta: { isOracle: true },
      });
    }

    const any = datasets.some((d) => d.data.some((v) => v != null));
    return { chartData: { labels, datasets }, hasData: any, hasPenalties: anyPenalty };
  }, [buckets, excludedIdx, penalties, colorMap, topN, traceActive, traceUserId, blurSensitive, showOracle, hiddenIds]);

  const selectedForecaster = useMemo(() => chartData.datasets.find((dataset) => (
    dataset.meta?.userId != null
    && !dataset.meta.isEnsemble
    && !dataset.meta.isOracle
    && String(dataset.meta.userId) === String(traceUserId)
  )) || null, [chartData, traceUserId]);

  const options = useMemo(() => {
    const textColor = dark ? '#e5e7eb' : '#374151';
    const gridColor = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
    // A line/point click traces that forecaster (toggles off if it's already
    // the spotlighted one), updating the matrix above through the shared
    // onTrace. The ensemble is not a forecaster, so it never traces.
    const traceFromElements = (elements) => {
      if (!onTrace || !elements || !elements.length) return;
      const meta = chartData.datasets[elements[0].datasetIndex]?.meta;
      const uid = meta?.userId;
      if (!uid || meta.isEnsemble) return;
      onTrace(uid === traceUserId ? null : uid);
    };
    // While spotlighting, cap the y-axis to the focused series (traced
    // forecaster + ensemble + oracle) so a dimmed outlier's spike doesn't
    // flatten the scale; the outlier simply clips off the top.
    let yMax;
    if (traceActive) {
      let focusMax = 0;
      chartData.datasets.forEach((d) => {
        const m = d.meta;
        if (!(m?.isEnsemble || m?.isOracle || m?.userId === traceUserId)) return;
        d.data.forEach((v) => {
          if (v != null && v > focusMax) focusMax = v;
        });
      });
      if (focusMax > 0) yMax = focusMax * 1.15;
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      // Index mode: hovering a bucket reads out every line at that tick,
      // sorted best-first (mirrors ErrorByLeadtimeChart). With a trace
      // active the tooltip filter below collapses it to the focused line.
      interaction: { mode: 'index', intersect: false },
      // Index mode hands every dataset at the tick to onClick/onHover, so
      // resolve the line nearest to the pointer ourselves.
      onClick: (evt, _elements, chart) => {
        if (!evt?.native) return;
        traceFromElements(chart.getElementsAtEventForMode(
          evt.native, 'nearest', { intersect: false }, true,
        ));
      },
      onHover: (evt, _elements, chart) => {
        if (!chart?.canvas || !evt?.native) return;
        const els = onTrace ? chart.getElementsAtEventForMode(
          evt.native, 'nearest', { intersect: true }, true,
        ) : [];
        const overForecaster = els.some((el) => {
          const m = chartData.datasets[el.datasetIndex]?.meta;
          return m && !m.isEnsemble && !m.isOracle;
        });
        chart.canvas.style.cursor = overForecaster ? 'pointer' : 'default';
      },
      plugins: {
        legend: { display: false },
        excludedDays: { indices: excludedIdx, dark },
        tooltip: {
          ...getChartTooltipStyle(dark),
          // Keep the tall readout away from the hovered (clickable) lines.
          position: 'cursorAway',
          itemSort: (a, b) => a.parsed.y - b.parsed.y,
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (v == null) return null;
              const pen = ctx.dataset.meta?.penaltyIdx?.includes(ctx.dataIndex);
              const suffix = pen ? ' (penalty · Q75)' : '';
              return `${ctx.dataset.label}: ${Number(v).toFixed(2)}${suffix}`;
            },
            // How many lines the per-tick readout was truncated by.
            footer: (items) => {
              if (traceActive || !items.length) return '';
              const idx = items[0].dataIndex;
              const total = chartData.datasets.filter(
                (d) => d.data[idx] != null,
              ).length;
              const hidden = total - items.length;
              return hidden > 0 ? `+${hidden} more (click a line to spotlight)` : '';
            },
          },
          // With a trace active, read out only the focused line + ensemble +
          // oracle. Otherwise cap the per-tick list to the best performers
          // (plus ensemble and oracle) so the tooltip fits inside the canvas.
          filter: (item, _index, items) => {
            if (item.parsed.y == null) return false;
            const m = item.dataset.meta;
            if (traceActive) {
              return m?.isEnsemble || m?.isOracle || m?.userId === traceUserId;
            }
            if (m?.isEnsemble || m?.isOracle) return true;
            // Rank by value with datasetIndex as tiebreaker: penalty days all
            // share the peer-Q75 charge, and a plain value cutoff would admit
            // every tie and overflow the canvas again.
            const idx = item.dataIndex;
            const ranked = items
              .filter((it) => {
                const im = it.dataset.meta;
                return it.parsed.y != null && it.dataIndex === idx
                  && !im?.isEnsemble && !im?.isOracle;
              })
              .sort((a, b) => (
                (a.parsed.y - b.parsed.y) || (a.datasetIndex - b.datasetIndex)
              ));
            const rank = ranked.findIndex(
              (it) => it.datasetIndex === item.datasetIndex,
            );
            return rank > -1 && rank < TOOLTIP_MAX_ROWS;
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: textColor,
            maxRotation: 0,
            autoSkip: true,
            font: { size: 10 },
          },
        },
        y: {
          beginAtZero: true,
          max: yMax,
          grid: { color: gridColor },
          ticks: { color: textColor, font: { size: 10 } },
          title: {
            display: true,
            text: `${metricLabel} error`,
            color: textColor,
            font: { size: 11 },
          },
        },
      },
    };
  }, [dark, metricLabel, traceActive, traceUserId, onTrace, chartData, excludedIdx]);

  return (
    <section>
      {/* Gap to the card lives on the title block (pb-3), not the header,
          so a taller right-side stat block cannot stretch it. */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 pb-3">
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
            Errors
          </div>
          <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
            Forecasting error over time
          </h3>
        </div>
        <div className="flex flex-wrap items-start justify-end gap-3">
          {headerRight || (
            <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-gray-500 font-mono flex-shrink-0 mt-1">
              {metricLabel} · {data?.grain || 'day'} · {TZ_LABEL}
            </span>
          )}
          {selectedForecaster && onHideUser && (
            <button
              type="button"
              onClick={() => onHideUser(selectedForecaster.meta.userId)}
              title={`Hide ${selectedForecaster.label} from both error plots`}
              className="max-w-[220px] truncate rounded-full border border-gray-300 bg-surface px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-300 dark:hover:border-brand-500/60 dark:hover:bg-brand-500/10 dark:hover:text-brand-300"
            >
              Hide {selectedForecaster.label}
            </button>
          )}
          {hiddenUserIds.length > 0 && (
            <button
              type="button"
              onClick={onRestoreHidden}
              className="rounded-full border border-brand-300 bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700 transition-colors hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-brand-500/60 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20"
            >
              <span className="font-mono tabular-nums">{hiddenUserIds.length}</span>
              {' hidden · Restore'}
            </button>
          )}
        </div>
      </header>
      <div className="bg-surface dark:bg-gray-870 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-600/70 overflow-hidden">
        <div className="p-5">
          {!hasData ? (
            <div className="h-48 rounded-md flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              No error data for this period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ height: 300, minWidth: 480 }}>
                <Line data={chartData} options={options} plugins={[excludedDaysPlugin]} />
              </div>
            </div>
          )}
          <p className="mt-3 pl-[30px] flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-gray-400">
            <span>
              {`Forecasters who reached the top ${topN}; lower is better.${traceActive ? ' Spotlighting the selected forecaster; use the Hide action above to remove it.' : ' Click a forecaster to spotlight them.'}`}
            </span>
            {hasPenalties && (
              <span className="inline-flex items-center gap-1.5">
                <span className="font-semibold text-slate-500 dark:text-gray-400">✕</span>
                Penalty day, charged at the peer Q75.
              </span>
            )}
            {excludedIdx.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className={`inline-block h-2.5 w-2.5 rounded-sm ${EXCLUDED_HATCH_CLASS}`} />
                Excluded day, left out of rankings and payments.
              </span>
            )}
            {showOracle && (
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-0 w-4 border-t-2 border-dotted"
                  style={{ borderColor: ORACLE_COLOR }}
                />
                Oracle = the daily best-forecaster error.
              </span>
            )}
          </p>
        </div>
      </div>
    </section>
  );
}
